import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, MessageCircle, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import { useCall } from '../contexts/CallContext';
import {
  useAcceptChatRequest,
  useAcceptGroupInvite,
  filterChatDirectory,
  useChatUserSearch,
  useConversationMessages,
  useConversations,
  useCreateGroup,
  useDeclineChatRequest,
  useDeclineGroupInvite,
  useDeleteConversation,
  useDeleteGroup,
  useDeleteMessage,
  useEditMessage,
  useGroupInvites,
  useIncomingChatRequests,
  useLeaveGroup,
  useMarkAsRead,
  useOutgoingChatRequests,
  usePinConversation,
  usePinMessage,
  useReopenConversation,
  useSendChatRequest,
  useSendMessage,
  useUploadChatImage,
} from '../hooks/api';
import ConversationList from './chat/ConversationList';
import ChatWindow from './chat/ChatWindow';
import Avatar from './Avatar';

function requestToListItem(req, direction) {
  const isIncoming = direction === 'incoming';
  const other = isIncoming
    ? {
        id: req.from_user_id,
        full_name: req.from_user_name,
        avatar: req.from_user_avatar,
      }
    : {
        id: req.to_user_id,
        full_name: req.to_user_name,
        avatar: req.to_user_avatar,
      };

  return {
    id: `request:${req.id}`,
    kind: 'pending_request',
    requestDirection: direction,
    request: { ...req, direction },
    other_user: other,
    preview: req.message || (isIncoming ? 'Wants to chat with you' : 'Request sent · Waiting'),
    last_message: req.message
      ? { text: req.message, sent_at: req.created_at, type: 'text' }
      : null,
    created_at: req.created_at,
    updated_at: req.updated_at || req.created_at,
    unread_count: isIncoming ? 1 : 0,
  };
}

const Chat = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState('list'); // list | chat | new
  const {
    sendMessage: wsSend,
    subscribe,
    connectionStatus,
    setActiveConversationId,
  } = useWebSocketContext();
  const { startCall } = useCall();
  const typingTimeoutRef = useRef(null);
  const activeIdRef = useRef(null);

  const {
    data: conversations = [],
    isLoading: loadingConvs,
    isError: convsError,
    refetch: refetchConvs,
  } = useConversations();
  const { data: incoming = [], isError: incomingError, refetch: refetchIncoming } =
    useIncomingChatRequests();
  const { data: outgoing = [], isError: outgoingError, refetch: refetchOutgoing } =
    useOutgoingChatRequests();
  const { data: incomingGroupInvites = [], refetch: refetchGroupIncoming } =
    useGroupInvites('incoming');
  const { data: outgoingGroupInvites = [], refetch: refetchGroupOutgoing } =
    useGroupInvites('outgoing');
  const acceptMutation = useAcceptChatRequest();
  const declineMutation = useDeclineChatRequest();
  const acceptGroupInvite = useAcceptGroupInvite();
  const declineGroupInvite = useDeclineGroupInvite();
  const createGroup = useCreateGroup();
  const pinConversation = usePinConversation();
  const pinMessage = usePinMessage();
  const leaveGroup = useLeaveGroup();
  const deleteGroup = useDeleteGroup();

  const activeConversationId =
    activeId &&
    !String(activeId).startsWith('request:') &&
    !String(activeId).startsWith('group-invite:')
      ? activeId
      : null;

  useEffect(() => {
    activeIdRef.current = activeId;
    setActiveConversationId?.(activeConversationId);
  }, [activeId, activeConversationId, setActiveConversationId]);

  const {
    data: messagesData,
    isLoading: loadingMessages,
    isError: messagesError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchMessages,
  } = useConversationMessages(activeConversationId, {
    enabled:
      !!activeConversationId &&
      !(conversations || []).find((c) => c.id === activeConversationId)?.has_left,
  });

  const messages = useMemo(() => {
    const pages = messagesData?.pages || [];
    const flattened = [...pages]
      .reverse()
      .flatMap((page) => page.messages || []);
    const seen = new Set();
    return flattened.filter((msg) => {
      if (!msg?.id || seen.has(msg.id)) return false;
      seen.add(msg.id);
      return true;
    });
  }, [messagesData]);

  const sendMutation = useSendMessage();
  const editMutation = useEditMessage();
  const deleteMessageMutation = useDeleteMessage();
  const deleteConversationMutation = useDeleteConversation();
  const uploadMutation = useUploadChatImage();
  const markRead = useMarkAsRead();
  const sendRequest = useSendChatRequest();
  const reopenConversation = useReopenConversation();

  const [listSearch, setListSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [introMessage, setIntroMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [typingUser, setTypingUser] = useState(null);
  const [presenceByUser, setPresenceByUser] = useState({});
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);

  // Prefetch directory as soon as Messages opens so New chat is instant
  const { data: chatDirectory = [], isFetching: searchingUsers } = useChatUserSearch(true);
  const searchUsers = useMemo(
    () => filterChatDirectory(chatDirectory, userSearch),
    [chatDirectory, userSearch]
  );

  const pendingIncoming = useMemo(
    () => (incoming || []).filter((r) => r.status === 'pending' || !r.status),
    [incoming]
  );
  const pendingOutgoing = useMemo(
    () => (outgoing || []).filter((r) => r.status === 'pending'),
    [outgoing]
  );

  const listItems = useMemo(() => {
    // Outgoing group invites stay on the group conversation — never as separate rows
    const pendingOutgoingByConv = {};
    for (const inv of outgoingGroupInvites || []) {
      const cid = inv.conversation_id;
      if (!cid) continue;
      if (!pendingOutgoingByConv[cid]) pendingOutgoingByConv[cid] = [];
      pendingOutgoingByConv[cid].push(inv);
    }

    const requestItems = [
      ...pendingIncoming.map((r) => requestToListItem(r, 'incoming')),
      ...pendingOutgoing.map((r) => requestToListItem(r, 'outgoing')),
      // Incoming only: invitee hasn't joined yet, so one row per invite
      ...(incomingGroupInvites || []).map((inv) => ({
        id: `group-invite:${inv.id}`,
        kind: 'group_invite',
        requestDirection: 'incoming',
        request: inv,
        name: inv.group_name || 'Group',
        description: inv.group_description || '',
        type: 'group',
        other_user: {
          id: inv.from_user_id,
          full_name: inv.from_user_name,
          avatar: inv.from_user_avatar,
        },
        preview:
          inv.group_description ||
          `${inv.from_user_name || 'Someone'} invited you`,
        created_at: inv.created_at,
        updated_at: inv.updated_at || inv.created_at,
        unread_count: 1,
      })),
    ];

    const convItems = (conversations || []).map((c) => {
      const pending_invites = pendingOutgoingByConv[c.id] || [];
      const names = pending_invites
        .map((inv) => inv.to_user_name)
        .filter(Boolean);
      let invitePreview = null;
      if (pending_invites.length === 1) {
        invitePreview = `Waiting for ${names[0] || '1 person'} to join`;
      } else if (pending_invites.length > 1) {
        invitePreview = `Waiting for ${pending_invites.length} people to join`;
      }
      return {
        ...c,
        kind: 'conversation',
        pending_invites,
        preview: invitePreview || undefined,
      };
    });

    return [...requestItems, ...convItems];
  }, [
    conversations,
    pendingIncoming,
    pendingOutgoing,
    incomingGroupInvites,
    outgoingGroupInvites,
  ]);

  const activeItem = listItems.find((item) => item.id === activeId) || null;
  const isPendingActive =
    activeItem?.kind === 'pending_request' || activeItem?.kind === 'group_invite';
  const showChatPane = view === 'chat' && !!activeItem;

  const otherUserId = activeItem?.other_user?.id;
  const otherPresence = otherUserId ? presenceByUser[otherUserId] : null;
  const isOtherOnline =
    otherPresence?.online ?? !!activeItem?.other_user?.is_online;
  const otherLastSeen =
    otherPresence?.last_seen ||
    activeItem?.other_user?.last_seen ||
    activeItem?.other_user?.chat_last_seen ||
    activeItem?.other_user?.last_login ||
    null;

  useEffect(() => {
    if (activeConversationId) {
      markRead.mutate(activeConversationId);
      wsSend({
        type: 'read_receipt',
        data: { conversation_id: activeConversationId },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    return subscribe((payload) => {
      if (payload?.type === 'typing') {
        const data = payload.data || {};
        // Prefer live active id from ref so typing still works after nav
        const openId = activeIdRef.current;
        if (data.conversation_id === openId && data.is_typing) {
          if (data.user_id && data.user_id === user?.id) return;
          setTypingUser(data.user_name || 'Someone');
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
        } else if (data.conversation_id === openId) {
          setTypingUser(null);
        }
      }
      if (payload?.type === 'presence') {
        const data = payload.data || {};
        if (!data.user_id) return;
        setPresenceByUser((prev) => ({
          ...prev,
          [data.user_id]: {
            online: !!data.online,
            last_seen: data.last_seen || prev[data.user_id]?.last_seen || null,
          },
        }));
      }
      if (payload?.type === 'request_accepted') {
        const req = payload.data?.request || {};
        const conversationId = payload.data?.conversation_id || req.conversation_id;
        const current = activeIdRef.current;
        if (conversationId && current && String(current).startsWith('request:')) {
          const reqId = String(current).replace(/^request:/, '');
          if (!req.id || req.id === reqId) {
            setActiveId(conversationId);
            setView('chat');
            toast.success('Chat request accepted — you can message now');
          }
        }
        queryClient.invalidateQueries({ queryKey: ['incomingChatRequests'] });
        queryClient.invalidateQueries({ queryKey: ['outgoingChatRequests'] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
      if (payload?.type === 'request_declined') {
        const req = payload.data?.request || {};
        const current = activeIdRef.current;
        if (current === `request:${req.id}`) {
          setActiveId(null);
          setView('list');
          toast('Chat request was declined');
        }
        queryClient.invalidateQueries({ queryKey: ['incomingChatRequests'] });
        queryClient.invalidateQueries({ queryKey: ['outgoingChatRequests'] });
      }
      if (payload?.type === 'request_received') {
        queryClient.invalidateQueries({ queryKey: ['incomingChatRequests'] });
      }
      if (payload?.type === 'group_invite_received') {
        queryClient.invalidateQueries({ queryKey: ['groupInvites'] });
      }
      if (payload?.type === 'group_invite_accepted') {
        const conversationId = payload.data?.conversation_id;
        const invite = payload.data?.invite || {};
        const name = invite.to_user_name || 'Someone';
        if (conversationId && activeIdRef.current === conversationId) {
          toast.success(`${name} joined the group`);
        } else if (invite.id && activeIdRef.current === `group-invite:${invite.id}`) {
          if (conversationId) {
            setActiveId(conversationId);
            setView('chat');
          }
          toast.success('Joined group');
        } else if (name) {
          toast.success(`${name} joined the group`);
        }
        queryClient.invalidateQueries({ queryKey: ['groupInvites'] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
      if (payload?.type === 'group_invite_declined') {
        const invite = payload.data?.invite || {};
        const name = invite.to_user_name || 'Someone';
        if (invite.conversation_id && activeIdRef.current === invite.conversation_id) {
          toast(`${name} declined the invite`);
        }
        queryClient.invalidateQueries({ queryKey: ['groupInvites'] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
      if (payload?.type === 'group_deleted') {
        const conversationId = payload.data?.conversation_id;
        if (conversationId && activeIdRef.current === conversationId) {
          setActiveId(null);
          setView('list');
          toast('Group was deleted');
        }
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
      if (payload?.type === 'new_message') {
        const message = payload.data?.message;
        const conversationId =
          payload.data?.conversation_id || message?.conversation_id;
        if (
          conversationId &&
          conversationId === activeIdRef.current &&
          message?.sender_id !== user?.id
        ) {
          setTypingUser(null);
          markRead.mutate(conversationId);
        }
      }
    });
  }, [subscribe, queryClient, user?.id, markRead]);

  const handleSelectItem = (item) => {
    setActiveId(item.id);
    setView('chat');
  };

  const handleSend = async ({ content, type, image_url, mentions, metadata }) => {
    if (!activeConversationId || isPendingActive) return;
    try {
      // Optimistic UI in useSendMessage — mutateAsync still awaits network for errors
      await sendMutation.mutateAsync({
        conversationId: activeConversationId,
        content,
        type,
        image_url,
        mentions,
        metadata,
        senderId: user?.id,
        senderName: user?.full_name || user?.username || 'You',
        senderAvatar: user?.avatar || null,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
      throw err;
    }
  };

  const handleEditMessage = async ({ content, mentions, editingMessageId }) => {
    if (!activeConversationId || !editingMessageId) return;
    try {
      await editMutation.mutateAsync({
        conversationId: activeConversationId,
        messageId: editingMessageId,
        content,
        mentions,
      });
      toast.success('Message updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to edit message');
      throw err;
    }
  };

  const handleDeleteMessage = async (msg) => {
    if (!activeConversationId || !msg?.id) return;
    try {
      await deleteMessageMutation.mutateAsync({
        conversationId: activeConversationId,
        messageId: msg.id,
      });
      toast.success('Message deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete message');
    }
  };

  const handleDeleteConversation = async (conversation) => {
    const conversationId = conversation?.id || activeConversationId;
    if (
      !conversationId ||
      String(conversationId).startsWith('request:') ||
      String(conversationId).startsWith('group-invite:')
    ) {
      return;
    }
    try {
      if (conversation?.type === 'group') {
        // Must leave first; if still a member, leave then allow delete for creator
        if ((conversation.participant_ids || []).includes(user?.id)) {
          toast.error('Leave the group before deleting it');
          return;
        }
        await deleteGroup.mutateAsync(conversationId);
        toast.success('Group deleted');
      } else {
        await deleteConversationMutation.mutateAsync(conversationId);
        toast.success('Conversation deleted');
      }
      setActiveId(null);
      setView('list');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete conversation');
    }
  };

  const handlePinConversation = async (conversation) => {
    if (!conversation?.id) return;
    try {
      await pinConversation.mutateAsync({
        conversationId: conversation.id,
        pinned: !conversation.is_pinned,
      });
      toast.success(conversation.is_pinned ? 'Chat unpinned' : 'Chat pinned');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pin');
    }
  };

  const handlePinMessage = async (msg, pinned) => {
    if (!activeConversationId || !msg?.id) return;
    try {
      await pinMessage.mutateAsync({
        conversationId: activeConversationId,
        messageId: msg.id,
        pinned,
      });
      toast.success(pinned ? 'Message pinned' : 'Message unpinned');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pin');
    }
  };

  const handleLeaveGroup = async (conversation) => {
    if (!conversation?.id) return;
    try {
      await leaveGroup.mutateAsync(conversation.id);
      const isCreator = conversation.created_by === user?.id;
      toast.success(
        isCreator
          ? 'Left group — you can delete it from your list'
          : 'Left group'
      );
      if (isCreator) {
        setView('chat');
        refetchConvs();
      } else {
        setActiveId(null);
        setView('list');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave group');
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Enter a group name');
      return;
    }
    try {
      const conv = await createGroup.mutateAsync({
        name: groupName.trim(),
        description: groupDescription.trim(),
        member_ids: selectedGroupMembers.map((m) => m.id),
      });
      toast.success(
        selectedGroupMembers.length
          ? 'Group created — invites sent'
          : 'Group created'
      );
      setGroupName('');
      setGroupDescription('');
      setSelectedGroupMembers([]);
      setUserSearch('');
      setActiveId(conv.id);
      setView('chat');
      refetchGroupOutgoing();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create group');
    }
  };

  const handleAcceptRequest = async (req) => {
    const isGroupInvite =
      activeItem?.kind === 'group_invite' || !!req?.group_name;
    if (isGroupInvite) {
      const inviteId = req?.id || activeItem?.request?.id;
      if (!inviteId) return;
      try {
        const result = await acceptGroupInvite.mutateAsync(inviteId);
        toast.success('Joined group');
        if (result?.id) {
          setActiveId(result.id);
          setView('chat');
        }
        refetchGroupIncoming();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to accept invite');
      }
      return;
    }
    const requestId = req?.id;
    if (!requestId) return;
    try {
      const result = await acceptMutation.mutateAsync(requestId);
      toast.success('Chat request accepted');
      if (result?.conversation_id) {
        setActiveId(result.conversation_id);
        setView('chat');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept request');
    }
  };

  const handleDeclineRequest = async (req) => {
    if (activeItem?.kind === 'group_invite') {
      const inviteId = req?.id || activeItem?.request?.id;
      if (!inviteId) return;
      try {
        await declineGroupInvite.mutateAsync(inviteId);
        toast.success('Invite declined');
        setActiveId(null);
        setView('list');
        refetchGroupIncoming();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to decline invite');
      }
      return;
    }
    const requestId = req?.id;
    if (!requestId) return;
    try {
      await declineMutation.mutateAsync(requestId);
      toast.success('Chat request declined');
      setActiveId(null);
      setView('list');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline request');
    }
  };

  const handleTyping = useCallback(
    (isTyping) => {
      if (!activeConversationId || isPendingActive) return;
      wsSend({
        type: 'typing',
        data: { conversation_id: activeConversationId, is_typing: isTyping },
      });
    },
    [activeConversationId, isPendingActive, wsSend]
  );

  const handleUpload = async (file) => {
    if (!activeConversationId || isPendingActive) return null;
    return uploadMutation.mutateAsync({ conversationId: activeConversationId, file });
  };

  const openDirectWithUser = async (u) => {
    try {
      const result = await reopenConversation.mutateAsync({
        conversationId: u.conversation_id || undefined,
        toUserId: u.id,
      });
      const conversationId = result?.id || result?.conversation_id;
      if (!conversationId) {
        toast.error('Could not open conversation');
        return;
      }
      await refetchConvs();
      setSelectedUser(null);
      setIntroMessage('');
      setUserSearch('');
      setActiveId(conversationId);
      setView('chat');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open conversation');
    }
  };

  const handleSendRequest = async () => {
    if (!selectedUser) return;
    // Already accepted / soft-deleted DM — never send a new invite
    if (selectedUser.can_message_directly || selectedUser.conversation_id) {
      await openDirectWithUser(selectedUser);
      return;
    }
    try {
      const result = await sendRequest.mutateAsync({
        to_user_id: selectedUser.id,
        message: introMessage,
      });
      if (result.conversation_id) {
        await refetchConvs();
        setActiveId(result.conversation_id);
        setView('chat');
        toast.success(
          result.status === 'accepted' ? 'Conversation ready' : 'Chat request sent'
        );
      } else if (result.id) {
        setActiveId(`request:${result.id}`);
        setView('chat');
        toast.success('Chat request sent');
      } else {
        toast.success('Chat request sent');
        setView('list');
      }
      setSelectedUser(null);
      setIntroMessage('');
      setUserSearch('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    }
  };

  return (
    <div
      className="flex flex-col h-[calc(100vh-4rem)] h-[calc(100dvh-4rem)] lg:h-[calc(100vh-5rem)] lg:h-[calc(100dvh-5rem)] w-full bg-white dark:bg-secondary-900 overflow-hidden"
      data-testid="chat-page"
      aria-label="Messages"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-200 dark:border-secondary-700 shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-primary-600" />
          <h1 className="text-base font-semibold text-secondary-900 dark:text-secondary-100">
            Messages
          </h1>
          <span
            className={`h-2 w-2 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-500' : 'bg-secondary-400'
            }`}
            title={connectionStatus}
            aria-label={`Connection ${connectionStatus}`}
          />
          {pendingIncoming.length > 0 && (
            <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[11px] font-semibold">
              {pendingIncoming.length} pending
            </span>
          )}
        </div>
      </div>

      {(convsError || incomingError || outgoingError) && (
        <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-center justify-between gap-3">
          <span>Couldn’t load messages. Check your connection and try again.</span>
          <button
            type="button"
            onClick={() => {
              refetchConvs();
              refetchIncoming();
              refetchOutgoing();
            }}
            className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-medium hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        <div
          className={`${
            showChatPane ? 'hidden lg:flex' : 'flex'
          } flex-col w-full lg:w-[320px] xl:w-[360px] lg:shrink-0 border-r border-secondary-200 dark:border-secondary-700 min-h-0 min-w-0`}
        >
          {view === 'new' ? (
            <div className="flex-1 flex flex-col min-h-0" data-testid="new-chat-panel">
              <div className="p-3 border-b border-secondary-200 dark:border-secondary-700 space-y-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUser(null);
                      setUserSearch('');
                      setView('list');
                    }}
                    className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800"
                    aria-label="Back to conversations"
                  >
                    <ArrowLeft className="h-5 w-5 text-secondary-600 dark:text-secondary-300" />
                  </button>
                  <h2 className="text-base font-semibold text-secondary-900 dark:text-secondary-100">
                    New chat
                  </h2>
                </div>
                <input
                  type="search"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search name…"
                  className="w-full px-3 py-2.5 min-h-[44px] rounded-lg border border-secondary-200 bg-secondary-50 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-100"
                  autoFocus
                />
              </div>
              <div className="flex-1 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUser(null);
                    setGroupName('');
                    setGroupDescription('');
                    setSelectedGroupMembers([]);
                    setUserSearch('');
                    setView('new-group');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 min-h-[56px] hover:bg-secondary-50 dark:hover:bg-secondary-800 text-left border-b border-secondary-100 dark:border-secondary-700/60"
                  data-testid="new-group-button"
                >
                  <div className="h-9 w-9 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center shrink-0">
                    <Users className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                    New group
                  </span>
                </button>
                <p className="px-3 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-secondary-400">
                  People
                  {searchUsers.length > 0 ? ` · ${searchUsers.length}` : ''}
                </p>
                {searchingUsers && searchUsers.length === 0 && (
                  <div className="p-4 text-sm text-secondary-500 text-center">Loading…</div>
                )}
                {!searchingUsers && searchUsers.length === 0 && (
                  <div className="p-4 text-sm text-secondary-500 text-center">
                    {userSearch.trim() ? 'No people match your search' : 'No people found'}
                  </div>
                )}
                {searchUsers.map((u) => {
                  const canOpenDirect =
                    !!u.conversation_id || !!u.can_message_directly;
                  const status = u.has_pending_request
                    ? 'Request pending'
                    : canOpenDirect
                      ? 'Message'
                      : u.username
                        ? `@${u.username}`
                        : null;
                  return (
                    <button
                      key={u.id}
                      type="button"
                      disabled={reopenConversation.isPending}
                      onClick={() => {
                        if (canOpenDirect) {
                          openDirectWithUser(u);
                          return;
                        }
                        if (u.has_pending_request) {
                          const match =
                            pendingOutgoing.find((r) => r.to_user_id === u.id) ||
                            pendingIncoming.find(
                              (r) => r.from_user_id === u.id || r.to_user_id === u.id
                            );
                          if (match) {
                            setActiveId(`request:${match.id}`);
                            setView('chat');
                          } else {
                            toast('Request already pending');
                          }
                          return;
                        }
                        setSelectedUser(u);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-3 min-h-[56px] hover:bg-secondary-50 dark:hover:bg-secondary-800 text-left disabled:opacity-50"
                    >
                      <Avatar
                        blobName={u.avatar}
                        alt={u.full_name || u.username || 'User'}
                        size="sm"
                        className="shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                          {u.full_name}
                        </p>
                        {status && (
                          <p className="text-xs text-secondary-500 truncate">{status}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedUser && (
                <div className="p-3 border-t border-secondary-200 dark:border-secondary-700 space-y-2">
                  <p className="text-sm text-secondary-700 dark:text-secondary-200">
                    Send request to <strong>{selectedUser.full_name}</strong>
                  </p>
                  <textarea
                    value={introMessage}
                    onChange={(e) => setIntroMessage(e.target.value)}
                    placeholder="Intro message (optional)"
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-secondary-200 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-100"
                  />
                  <button
                    type="button"
                    onClick={handleSendRequest}
                    disabled={sendRequest.isPending}
                    className="w-full py-2.5 min-h-[44px] rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    {sendRequest.isPending ? 'Sending…' : 'Send Request'}
                  </button>
                </div>
              )}
            </div>
          ) : view === 'new-group' ? (
            <div className="flex-1 flex flex-col min-h-0" data-testid="new-group-composer">
              <div className="p-3 border-b border-secondary-200 dark:border-secondary-700 space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setGroupName('');
                      setGroupDescription('');
                      setSelectedGroupMembers([]);
                      setUserSearch('');
                      setView('new');
                    }}
                    className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800"
                    aria-label="Back to new chat"
                  >
                    <ArrowLeft className="h-5 w-5 text-secondary-600 dark:text-secondary-300" />
                  </button>
                  <h2 className="text-base font-semibold text-secondary-900 dark:text-secondary-100">
                    New group
                  </h2>
                </div>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Group name"
                  maxLength={80}
                  className="w-full px-3 py-2.5 min-h-[44px] rounded-lg border border-secondary-200 bg-secondary-50 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-100"
                  data-testid="group-name-input"
                />
                <textarea
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Description (optional)"
                  maxLength={500}
                  rows={2}
                  className="w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-secondary-50 text-sm resize-none dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-100"
                  data-testid="group-description-input"
                />
                <input
                  type="search"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Invite people (optional)…"
                  className="w-full px-3 py-2.5 min-h-[44px] rounded-lg border border-secondary-200 bg-secondary-50 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-100"
                />
                {selectedGroupMembers.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedGroupMembers.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() =>
                          setSelectedGroupMembers((prev) =>
                            prev.filter((x) => x.id !== m.id)
                          )
                        }
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary-50 text-primary-700 text-xs dark:bg-primary-900/30 dark:text-primary-300"
                      >
                        {m.full_name || m.username}
                        <span aria-hidden>×</span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-secondary-500">
                  Up to 49 invites ({selectedGroupMembers.length}/49) · max 50 members
                </p>
              </div>
              <div className="flex-1 overflow-y-auto">
                {searchingUsers && searchUsers.length === 0 && (
                  <div className="p-4 text-sm text-secondary-500 text-center">Loading…</div>
                )}
                {!searchingUsers && searchUsers.length === 0 && (
                  <div className="p-4 text-sm text-secondary-500 text-center">
                    {userSearch.trim() ? 'No people match your search' : 'No people found'}
                  </div>
                )}
                {searchUsers.map((u) => {
                  const selected = selectedGroupMembers.some((m) => m.id === u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      disabled={!selected && selectedGroupMembers.length >= 49}
                      onClick={() => {
                        setSelectedGroupMembers((prev) => {
                          if (prev.some((m) => m.id === u.id)) {
                            return prev.filter((m) => m.id !== u.id);
                          }
                          if (prev.length >= 49) return prev;
                          return [...prev, u];
                        });
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-3 min-h-[56px] text-left hover:bg-secondary-50 dark:hover:bg-secondary-800 disabled:opacity-40 ${
                        selected ? 'bg-primary-50/60 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <Avatar
                        blobName={u.avatar}
                        alt={u.full_name || u.username || 'User'}
                        size="sm"
                        className="shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                          {u.full_name}
                        </p>
                        {u.username && (
                          <p className="text-xs text-secondary-500 truncate">@{u.username}</p>
                        )}
                      </div>
                      {selected && (
                        <span className="text-xs font-medium text-primary-600">Selected</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="p-3 border-t border-secondary-200 dark:border-secondary-700">
                <button
                  type="button"
                  onClick={handleCreateGroup}
                  disabled={createGroup.isPending || !groupName.trim()}
                  className="w-full py-2.5 min-h-[44px] rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  data-testid="create-group-submit"
                >
                  {createGroup.isPending ? 'Creating…' : 'Create Group'}
                </button>
              </div>
            </div>
          ) : (
            <ConversationList
              items={listItems}
              activeId={activeId}
              onSelect={handleSelectItem}
              onNewChat={() => {
                setSelectedUser(null);
                setUserSearch('');
                setView('new');
              }}
              search={listSearch}
              onSearchChange={setListSearch}
              loading={loadingConvs}
            />
          )}
        </div>

        <div
          className={`${
            showChatPane ? 'flex' : 'hidden lg:flex'
          } flex-1 flex-col min-h-0 min-w-0`}
        >
          {messagesError && !isPendingActive && activeConversationId && (
            <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200 flex items-center justify-between gap-3">
              <span>Couldn’t load message history.</span>
              <button
                type="button"
                onClick={() => refetchMessages()}
                className="px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700"
              >
                Retry
              </button>
            </div>
          )}
          <ChatWindow
            conversation={activeItem}
            messages={isPendingActive || activeItem?.has_left ? [] : messages}
            loading={!isPendingActive && !activeItem?.has_left && loadingMessages}
            loadingOlder={isFetchingNextPage}
            hasMoreOlder={
              !isPendingActive && !activeItem?.has_left && !!hasNextPage
            }
            onLoadOlder={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            typingUser={isPendingActive || activeItem?.has_left ? null : typingUser}
            isOnline={!isPendingActive && !activeItem?.has_left && isOtherOnline}
            lastSeen={otherLastSeen}
            onBack={() => setView('list')}
            onSend={handleSend}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
            onDeleteConversation={handleDeleteConversation}
            onPinConversation={handlePinConversation}
            onPinMessage={handlePinMessage}
            onLeaveGroup={handleLeaveGroup}
            onStartCall={(callType) => {
              if (!activeItem?.id || activeItem?.type === 'group') return;
              startCall(activeItem.id, callType, {
                id: activeItem?.other_user?.id,
                name:
                  activeItem?.other_user?.full_name ||
                  activeItem?.other_user?.username ||
                  'User',
                avatar: activeItem?.other_user?.avatar,
              });
            }}
            onTyping={handleTyping}
            onUploadImage={handleUpload}
            sending={uploadMutation.isPending || editMutation.isPending}
            deletingConversation={
              deleteConversationMutation.isPending || deleteGroup.isPending
            }
            deletingMessage={deleteMessageMutation.isPending}
            messagingEnabled={!isPendingActive && !activeItem?.has_left}
            pendingRequest={isPendingActive ? activeItem.request : null}
            onAcceptRequest={handleAcceptRequest}
            onDeclineRequest={handleDeclineRequest}
            acceptingRequest={
              acceptMutation.isPending || acceptGroupInvite.isPending
            }
            decliningRequest={
              declineMutation.isPending || declineGroupInvite.isPending
            }
          />
        </div>
      </div>
    </div>
  );
};

export default Chat;
